import React, { useRef, useCallback, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './TadsWM.module.css';
import { TadsWidget } from 'react-tads-widget';
import Loader from '../../components/Loader/Loader';

interface MemoizedTadsWidgetProps {
  onClickReward: () => void;
  onAdsNotFound?: () => void;
  onTadsClick?: (link: string) => void;
  checkOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

interface AdWidgetModalProps {
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
  onReward: () => void;
  onAdsNotFound: () => void;
  checkOnly?: boolean;
}

const MemoizedTadsWidget = memo(function MemoizedTadsWidget({
  onClickReward,
  onAdsNotFound,
  onTadsClick,
  checkOnly,
  onLoadingChange,
}: MemoizedTadsWidgetProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const adLoadedRef = useRef<boolean>(false);
  const clickedRef = useRef<boolean>(false);
  const adViewedRef = useRef<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    timeoutRef.current = setTimeout(
      () => {
        if (!adLoadedRef.current) {
          console.log('TadsWidget: Реклама не загрузилась за отведенное время');
          setIsLoading(false);
          if (onAdsNotFound) {
            onAdsNotFound();
          }
        }
      },
      checkOnly ? 3000 : 4000,
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onAdsNotFound, checkOnly]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  const handleShowReward = useCallback(() => {
    console.log('TadsWidget: onShowReward вызван');
    adLoadedRef.current = true;
    setIsLoading(false);

    if (checkOnly) {
      console.log('TadsWidget: Реклама доступна (проверка)');
      if (onClickReward) {
        onClickReward();
      }
    }
  }, [checkOnly, onClickReward]);

  const handleTadsClickWrapper = useCallback(
    (link: string) => {
      if (clickedRef.current) return;
      clickedRef.current = true;
      adViewedRef.current = true;

      console.log('TadsWidget: onTadsClick вызван с ссылкой:', link);

      if (!checkOnly) {
        console.log('TadsWidget: Вызываем reward после клика');
        onClickReward();
      }

      if (onTadsClick) {
        onTadsClick(link);
      }
    },
    [onClickReward, onTadsClick, checkOnly],
  );

  return (
    <div style={{ opacity: isLoading ? 0 : 1 }}>
      {isLoading && (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      )}
      <TadsWidget
        id="776"
        debug={true}
        onClickReward={handleTadsClickWrapper}
        onShowReward={handleShowReward}
      />
    </div>
  );
});

const AdWidgetModal = memo(function AdWidgetModal({
  isOpen,
  isClosing,
  onClose,
  onReward,
  onAdsNotFound,
  checkOnly,
}: AdWidgetModalProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const handleCloseClick = useCallback(() => {
    console.log('AdWidgetModal: Закрытие модального окна');
    onClose();
  }, [onClose]);

  const handleReward = useCallback(() => {
    console.log('AdWidgetModal: Вызов reward');
    onReward();
  }, [onReward]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`${styles.modalWrapper}`}
      style={{ opacity: isLoading ? 0 : 1 }}
    >
      <div className={`${styles.modal} ${isClosing ? styles.slideDown : ''}`}>
        <div className={styles.modalContent}>
          {!checkOnly && (
            <button
              className={styles.closeButton}
              onClick={handleCloseClick}
            ></button>
          )}
          {!checkOnly && <p className={styles.rewardText}>Нажми на баннер</p>}
          {!checkOnly && (
            <p className={styles.rewardTextSmall}>
              Кликни, чтобы задание сразу засчиталось!
            </p>
          )}
          <div className={styles.tadsWidgetWrapper}>
            <MemoizedTadsWidget
              onClickReward={handleReward}
              onAdsNotFound={onAdsNotFound}
              checkOnly={checkOnly}
              onLoadingChange={setIsLoading}
            />
          </div>
          {!checkOnly && (
            <div className={styles.rewardsModal}>
              <p className={styles.rewardText_warning}>
                *Остерегайтесь мошенников. <br /> Подписываться необязательно.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
});

export default AdWidgetModal;
